import React, { useState, useRef, useCallback } from 'react';
import API_URL from '../config';
import axios from 'axios';
import './FaKnowledgeUploadModal.css';

function FaKnowledgeUploadModal({ companies, onClose, onUploaded }) {
  const [companyId, setCompanyId] = useState('');
  const [candidateName, setCandidateName] = useState('');
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const validateAndSetFile = useCallback((fileList) => {
    const picked = fileList?.[0];
    if (!picked) return;
    if (picked.type !== 'application/pdf') {
      setError('Only PDF files are supported.');
      return;
    }
    setError(null);
    setFile(picked);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    validateAndSetFile(e.dataTransfer.files);
  }, [validateAndSetFile, uploading]);

  const handleFileInput = (e) => {
    validateAndSetFile(e.target.files);
    e.target.value = '';
  };

  const handleUpload = async () => {
    if (!companyId) { setError('Please select a company.'); return; }
    if (!candidateName.trim()) { setError('Please enter the candidate name.'); return; }
    if (!file) { setError('Please add a PDF transcript.'); return; }

    setError(null);
    setUploading(true);

    const formData = new FormData();
    formData.append('transcript', file);
    formData.append('company_id', companyId);
    formData.append('candidate_name', candidateName.trim());

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/fa-knowledge/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      onUploaded();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed.');
    }
    setUploading(false);
  };

  return (
    <div className="fku-overlay" onClick={uploading ? null : onClose}>
      <div className="fku-modal" onClick={e => e.stopPropagation()}>
        <div className="fku-header">
          <h2>Upload Transcript</h2>
          {!uploading && <button className="fku-close" onClick={onClose}>✕</button>}
        </div>

        <div className="fku-body">
          <div className="fku-field">
            <label>Company <span className="fku-required">*</span></label>
            <select value={companyId} onChange={e => setCompanyId(e.target.value)} disabled={uploading}>
              <option value="">Select a company...</option>
              {companies.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="fku-field">
            <label>Candidate <span className="fku-required">*</span> <span className="fku-hint">(write names comma separated for multiple candidate chats)</span></label>
            <input
              type="text"
              value={candidateName}
              onChange={e => setCandidateName(e.target.value)}
              placeholder="Candidate's full name"
              disabled={uploading}
            />
          </div>

          <div
            className={`fku-drop-zone ${dragOver ? 'drag-over' : ''} ${uploading ? 'disabled' : ''}`}
            onDragOver={e => { e.preventDefault(); if (!uploading) setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={uploading ? e => e.preventDefault() : handleDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              accept=".pdf,application/pdf"
              onChange={handleFileInput}
              style={{ display: 'none' }}
            />
            <div className="fku-drop-icon">📄</div>
            {file ? (
              <>
                <p className="fku-drop-text">{file.name}</p>
                <p className="fku-drop-hint">{(file.size / 1024).toFixed(0)} KB · Click or drop to replace</p>
              </>
            ) : (
              <>
                <p className="fku-drop-text">{dragOver ? 'Drop transcript here' : 'Drag & drop the transcript here, or click to browse'}</p>
                <p className="fku-drop-hint">PDF files only</p>
              </>
            )}
          </div>

          {error && <div className="fku-error">{error}</div>}

          {uploading && (
            <div className="fku-progress">
              <div className="fku-spinner" />
              <p>AI is reading and summarizing the transcript...</p>
            </div>
          )}
        </div>

        <div className="fku-footer">
          {!uploading && <button className="fku-btn fku-btn-cancel" onClick={onClose}>Cancel</button>}
          <button
            className="fku-btn fku-btn-primary"
            onClick={handleUpload}
            disabled={uploading || !companyId || !candidateName.trim() || !file}
          >
            {uploading ? 'Processing...' : 'Upload Transcript'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default FaKnowledgeUploadModal;
